import { clsx } from "clsx";
import { useId, useRef, useState } from "react";
import { STATE_META } from "../lib/stat-cell";
import { useDismiss } from "./facet-dropdown";

// The four ADR 0003 states as one compact legend (Issue 06; copy tightened by the user
// 2026-07-28). The state language is STATE_META verbatim and the glyph treatments mirror
// StatCellView exactly.
const LEGEND_ROWS: { glyph: React.ReactNode; label: string; description: string }[] = [
  {
    glyph: <span className="text-ink">1,234</span>,
    label: STATE_META.computed.label,
    description: STATE_META.computed.description,
  },
  {
    glyph: (
      <span className="text-muted-2 underline decoration-dotted underline-offset-2">
        {STATE_META.not_applicable.glyph}
      </span>
    ),
    label: STATE_META.not_applicable.label,
    description: STATE_META.not_applicable.description,
  },
  {
    glyph: (
      <span className="text-unverified underline decoration-dotted underline-offset-2">
        {STATE_META.formula_unverified.glyph}
      </span>
    ),
    label: STATE_META.formula_unverified.label,
    description: STATE_META.formula_unverified.description,
  },
  {
    glyph: (
      <span className="text-muted-2 underline decoration-dotted underline-offset-2">
        {STATE_META.not_in_game_data.glyph}
      </span>
    ),
    label: STATE_META.not_in_game_data.label,
    description: STATE_META.not_in_game_data.description,
  },
];

/** The legend rows alone — embedded in the roster's "how are these stats computed"
 * disclosure so the states are explained in exactly one place. */
export function StatesLegendRows({ className }: { className?: string }) {
  return (
    <dl className={clsx("grid grid-cols-[auto_auto_1fr] gap-x-3 gap-y-2", className)}>
      {LEGEND_ROWS.map((row) => (
        <div key={row.label} className="contents">
          <dt className="text-right font-mono text-[13px] [font-variant-numeric:tabular-nums]">
            {row.glyph}
          </dt>
          <dt className="whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.08em] text-ink-2">
            {row.label}
          </dt>
          <dd className="text-[12.5px] text-ink-3">{row.description}</dd>
        </div>
      ))}
    </dl>
  );
}

/** The legend as a quiet popover trigger — sits beside any table or stat sheet so the
 * four states are explainable wherever stat cells appear. Dismisses like every other
 * popover (outside click, Escape). The panel is right-anchored, so place the trigger at
 * the right edge of its line or the panel walks off a narrow viewport's left side.
 * The frame-assumption bottom note was removed by user decision (2026-07-29): the
 * Base / Fully-Teched toggle plus each page's frame declaration already say it. */
export function StatesLegend() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  useDismiss(open, rootRef, () => setOpen(false));

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((prev) => !prev)}
        className={clsx(
          "cursor-pointer font-mono text-[11px] uppercase tracking-[0.08em] underline decoration-dotted underline-offset-2",
          open ? "text-ink-2" : "text-muted-2 hover:text-ink-3",
        )}
      >
        Legend
      </button>
      {open && (
        <div
          id={panelId}
          className="absolute right-0 top-full z-20 mt-1 w-[min(420px,calc(100vw-2rem))] rounded-lg border border-line-strong bg-surface p-3 shadow-[0_8px_24px_rgba(0,0,0,0.5)]"
        >
          <StatesLegendRows />
        </div>
      )}
    </div>
  );
}
